/**
 * Schémas Zod pour la validation des données localStorage (format client).
 * Distinct des schémas serveur (local.schema.ts vs task.schema.ts).
 */
import { z } from "zod/v4";

const categoryLocalSchema = z.string().min(1);
const taskStatusLocalSchema = z.enum(["todo", "doing", "done"]);
const priorityLocalSchema = z.enum(["urgent", "normal", "low"]);

const recurrenceLocalSchema = z.object({
  type: z.enum(["daily", "weekly", "monthly", "quarterly", "semiannual", "yearly"]),
  interval: z.number().int().positive(),
  endDate: z.string().nullable(),
  daysOfWeek: z.array(z.number().int().min(0).max(6)).optional(),
});

const subtaskLocalSchema = z.object({
  id: z.string(),
  title: z.string(),
  done: z.boolean(),
});

const eventSubtaskLocalSchema = z.object({
  id: z.string(),
  title: z.string(),
  done: z.boolean().optional().default(false),
  priority: z.number(),
});

/** Accepts both a timestamp number and an ISO date string (from Prisma). */
const createdAtSchema = z
  .union([z.number(), z.string()])
  .transform((v) => (typeof v === "string" ? new Date(v).getTime() : v))
  .default(() => Date.now());

export const localTaskSchema = z.object({
  id: z.string(),
  title: z.string().default(""),
  category: categoryLocalSchema.default("personal"),
  status: taskStatusLocalSchema.default("todo"),
  priority: priorityLocalSchema.default("normal"),
  createdAt: createdAtSchema,
  dueDate: z.string().nullable().default(null),
  notes: z.string().default(""),
  tags: z.array(z.string()).default([]),
  subtasks: z.array(subtaskLocalSchema).default([]),
  archived: z.boolean().default(false),
  completedAt: z.union([z.number(), z.string()]).nullable().optional().default(null)
    .transform((v) => {
      if (v === null || v === undefined) return null;
      return typeof v === "string" ? new Date(v).getTime() : v;
    }),
  recurrence: recurrenceLocalSchema.nullable().default(null),
  eventId: z.string().nullable().optional().default(null),
  reminderMinutes: z.number().nullable().optional().default(null),
  reminderTime: z.string().nullable().optional().default(null),
  reminderType: z.enum(["BEFORE", "AT_TIME"]).nullable().optional().default(null),
});

export const localEventSchema = z.object({
  id: z.string(),
  title: z.string().default(""),
  category: categoryLocalSchema.default("personal"),
  date: z.string().default(""),
  startTime: z.string().default("09:00"),
  endTime: z.string().default("10:00"),
  createdAt: createdAtSchema,
  description: z.string().default(""),
  notes: z.string().default(""),
  tags: z.array(z.string()).default([]),
  recurrence: recurrenceLocalSchema.nullable().default(null),
  excludedDates: z.array(z.string()).default([]),
  detachedFromId: z.string().nullable().optional(),
  reminderMinutes: z.number().nullable().default(null),
  reminderTime: z.string().nullable().optional().default(null),
  reminderType: z.enum(["BEFORE", "AT_TIME"]).nullable().optional().default(null),
  allDay: z.boolean().default(false),
  eventSubtasks: z
    .array(eventSubtaskLocalSchema)
    .nullable()
    .transform((v) => v ?? [])
    .default([]),
});

export const localHabitSchema = z.object({
  id: z.string(),
  title: z.string().default(""),
  category: categoryLocalSchema.default("personal"),
  frequency: z.enum(["daily", "weekly"]).default("daily"),
  completedDates: z.array(z.string()).default([]),
  createdAt: createdAtSchema,
});

export const importDataSchema = z.object({
  tasks: z.array(z.unknown()),
  events: z.array(z.unknown()),
  habits: z.array(z.unknown()).optional(),
});

export type LocalTask = z.infer<typeof localTaskSchema>;
export type LocalEvent = z.infer<typeof localEventSchema>;
export type LocalHabit = z.infer<typeof localHabitSchema>;
